/**
 * Agent activity timeline — shows what ilmli's agentic capabilities did
 * recently. Tool calls, scheduled follow-ups + their delivery, weekly
 * reflections, impasse-detector nudges. Lang-agnostic; displays a flat
 * newest-first list.
 *
 * Empty state: "no agent activity yet" — happens for users on
 * pre-agent stacks or before tools have fired.
 */
import type { AgentEvent } from "../../api";

const TYPE_META: Record<
  string,
  { label: string; color: string; icon: string }
> = {
  tool_call:           { label: "tool call",           color: "#7b9458", icon: "⚙" },
  followup_scheduled:  { label: "follow-up scheduled", color: "#c88649", icon: "⏱" },
  followup_delivered:  { label: "follow-up sent",      color: "#3f6a44", icon: "→"  },
  drill_scheduled:     { label: "drill scheduled",     color: "#c88649", icon: "⏱" },
  drill_delivered:     { label: "drill sent",          color: "#3f6a44", icon: "→"  },
  weekly_review:       { label: "weekly review",       color: "#b8472c", icon: "✦" },
  impasse_nudge:       { label: "impasse re-armed",    color: "#9a968d", icon: "↻" },
};

function relTime(epoch: number): string {
  const sec = Math.max(1, Math.floor(Date.now() / 1000) - epoch);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function summary(ev: AgentEvent): string {
  const d = ev.detail || {};
  switch (ev.type) {
    case "tool_call": {
      const tool = String(d.tool || "?");
      const ok = d.ok !== false;
      return ok ? `the tutor called ${tool}` : `${tool} (failed)`;
    }
    case "followup_scheduled":
      return `seed: "${String(d.prompt_preview || "").slice(0, 60)}" · in ~${d.due_in_minutes ?? "?"} min`;
    case "followup_delivered":
    case "drill_delivered":
      return String(d.seed_preview || "").slice(0, 80) || "delivered";
    case "drill_scheduled": {
      const words = (d.words as string[]) || [];
      return words.length ? words.join(" · ") : "drill queued";
    }
    case "weekly_review":
      return String(d.text_preview || "").slice(0, 100);
    case "impasse_nudge": {
      const n = Number(d.count) || 0;
      const sample = (d.sample_words as string[]) || [];
      return `${n} stuck word${n === 1 ? "" : "s"} re-armed${
        sample.length ? ": " + sample.slice(0, 4).join(", ") : ""
      }`;
    }
    default:
      return ev.type;
  }
}

export default function AgentActivity({
  events,
}: {
  events: AgentEvent[] | undefined;
}) {
  if (!events || events.length === 0) {
    return (
      <div className="card">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
          agent activity
        </div>
        <div className="text-[11px] text-ink-faint mt-0.5">
          tools the tutor called and background reflections
        </div>
        <p className="mt-4 text-sm text-ink-faint">
          No agent activity yet. Once tools fire (when you ask "how am I
          doing?", request a drill, etc.) and the background agents run,
          they'll show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
            agent activity
          </div>
          <div className="text-[11px] text-ink-faint mt-0.5">
            tools the tutor called and background reflections — newest first
          </div>
        </div>
        <div className="font-serif text-2xl text-ink">
          {events.length}
          <span className="text-sm text-ink-faint ml-1">events</span>
        </div>
      </div>
      <div className="mt-2 space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
        {events.map((ev, i) => {
          const meta = TYPE_META[ev.type] || {
            label: ev.type,
            color: "#9a968d",
            icon: "·",
          };
          const lang = String((ev.detail || {}).lang || "");
          return (
            <div
              key={`${ev.epoch}-${i}`}
              className="flex items-start gap-3 text-xs py-1.5 border-b border-ink-faint/10 last:border-b-0"
            >
              <span
                className="font-mono mt-0.5 shrink-0 w-4 text-center"
                style={{ color: meta.color }}
                aria-hidden
              >
                {meta.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-medium"
                    style={{ color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  {lang ? (
                    <span className="text-[10px] uppercase tracking-wider text-ink-faint">
                      {lang}
                    </span>
                  ) : null}
                  <span className="text-[10px] text-ink-faint ml-auto shrink-0">
                    {relTime(ev.epoch)}
                  </span>
                </div>
                <div className="text-ink-soft mt-0.5 text-[12px] truncate">
                  {summary(ev)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
